// layouts/catalogos/parametros/nuevo-parametro.jsx

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Alert from "@mui/material/Alert";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";

import FormPage from "ui/FormPage";
import { PrimaryButton, OutlineButton } from "ui/Buttons";
import FormTextField from "ui/FormTextField";

import { listFormatos } from "../services/formatos";
import { createParametro } from "../services/parametros";

/* ---------------- helpers ---------------- */
function extractErrMessages(err) {
  const out = [];
  const d = err?.response?.data ?? err;
  const push = (m) => {
    if (!m && m !== 0) return;
    if (typeof m === "string") out.push(m);
    else if (Array.isArray(m)) m.forEach((it) => push(it?.message || it));
    else if (m?.errors && Array.isArray(m.errors))
      m.errors.forEach((it) =>
        push(
          `${it?.instancePath || it?.path || ""} ${
            it?.message || ""
          }`.trim()
        )
      );
    else if (m?.message) out.push(m.message);
    else out.push(String(m));
  };

  push(d);
  if (err?.response?.status) out.push(`HTTP ${err.response.status}`);
  return out.filter(Boolean);
}

const toIsoMidnight = (dateStr) => {
  const s = String(dateStr || "").trim();
  if (!s) return null;
  // asumimos formato YYYY-MM-DD desde <input type="date">
  return `${s}T00:00:00.000Z`;
};
/* ----------------------------------------- */

export default function NuevoParametro() {
  const navigate = useNavigate();

  const [formatId, setFormatId] = useState("");
  const [since, setSince] = useState("");
  const [until, setUntil] = useState("");
  const [gold, setGold] = useState("");
  const [platinum, setPlatinum] = useState("");
  const [diamond, setDiamond] = useState("");

  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState([]);

  const [formatos, setFormatos] = useState([]);
  const [loadingFormatos, setLoadingFormatos] = useState(true);

  // cargar formatos para el select
  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoadingFormatos(true);
      try {
        const res = await listFormatos();
        const rows = Array.isArray(res.rows ?? res)
          ? (res.rows ?? res)
          : res.data ?? res.page ?? [];
        if (!cancel) setFormatos(rows);
      } catch (e) {
        if (!cancel) setErrors((prev) => [...prev, ...extractErrMessages(e)]);
      } finally {
        if (!cancel) setLoadingFormatos(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, []);

  const goldNum = Number(gold);
  const platinumNum = Number(platinum);
  const diamondNum = Number(diamond);

  const isValid = useMemo(() => {
    if (!String(formatId).trim()) return false;
    if (!since || !until) return false;
    if (!Number.isFinite(goldNum) || goldNum <= 0) return false;
    if (!Number.isFinite(platinumNum) || platinumNum <= 0) return false;
    if (!Number.isFinite(diamondNum) || diamondNum <= 0) return false;
    return true;
  }, [formatId, since, until, goldNum, platinumNum, diamondNum]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!isValid) {
      const local = [];
      if (!String(formatId).trim()) local.push("El formato es obligatorio.");
      if (!since) local.push("La fecha 'desde' (since) es obligatoria.");
      if (!until) local.push("La fecha 'hasta' (until) es obligatoria.");
      if (!Number.isFinite(goldNum) || goldNum <= 0)
        local.push("Gold debe ser un número mayor a 0.");
      if (!Number.isFinite(platinumNum) || platinumNum <= 0)
        local.push("Platinum debe ser un número mayor a 0.");
      if (!Number.isFinite(diamondNum) || diamondNum <= 0)
        local.push("Diamond debe ser un número mayor a 0.");
      setErrors(local);
      return;
    }

    setSaving(true);
    setErrors([]);

    try {
      const payload = {
        format_id: Number(formatId),
        since: toIsoMidnight(since),
        until: toIsoMidnight(until),
        gold: goldNum,
        platinum: platinumNum,
        diamond: diamondNum,
        // created_at lo pone el backend
      };

      await createParametro(payload);
      navigate("/catalogos/parametros", { replace: true });
    } catch (e2) {
      const errs = extractErrMessages(e2);
      setErrors(errs.length ? errs : ["Error al guardar parámetros"]);
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormPage
      title="Nuevo parámetro"
      onBack={() => navigate("/catalogos/parametros")}
      pageMaxWidth={1100}
      contentMaxWidth={900}
      actions={
        <>
          <PrimaryButton
            type="submit"
            form="form-parametro"
            disabled={!isValid || saving}
          >
            {saving ? "Guardando..." : "Guardar"}
          </PrimaryButton>
          <OutlineButton onClick={() => navigate("/catalogos/parametros")}>
            Cancelar
          </OutlineButton>
        </>
      }
    >
      {errors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errors.length === 1
            ? errors[0]
            : (
              <ul style={{ margin: 0, paddingLeft: "1.2rem" }}>
                {errors.map((m, i) => (
                  <li key={i}>{m}</li>
                ))}
              </ul>
            )}
        </Alert>
      )}

      <form id="form-parametro" onSubmit={onSubmit}>
        <Grid container spacing={2}>
          {/* Formato */}
          <Grid item xs={12} md={6}>
            <TextField
              select
              fullWidth
              label=""
              value={String(formatId ?? "")}
              onChange={(e) => setFormatId(e.target.value)}
              required
              disabled={loadingFormatos}
              SelectProps={{ displayEmpty: true }}
            >
              <MenuItem value="" disabled>
                {loadingFormatos ? "Cargando formatos…" : "Selecciona un formato"}
              </MenuItem>
              {formatos.map((f) => (
                <MenuItem key={f.id ?? f._id} value={String(f.id ?? f._id)}>
                  {f.name ?? f.nombre ?? `Formato ${f.id ?? f._id}`}
                  {f.code ? ` (${f.code})` : ""}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Rango de fechas */}
          <Grid item xs={12} md={3}>
            <FormTextField
              label="Desde*"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={since}
              onChange={(e) => setSince(e.target.value)}
              required
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormTextField
              label="Hasta*"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={until}
              onChange={(e) => setUntil(e.target.value)}
              required
            />
          </Grid>

          {/* Umbrales */}
          <Grid item xs={12} md={4}>
            <FormTextField
              label="Gold*"
              type="number"
              value={gold}
              onChange={(e) => setGold(e.target.value)}
              inputProps={{ min: 1, step: 1 }}
              required
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormTextField
              label="Platinum*"
              type="number"
              value={platinum}
              onChange={(e) => setPlatinum(e.target.value)}
              inputProps={{ min: 1, step: 1 }}
              required
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormTextField
              label="Diamond*"
              type="number"
              value={diamond}
              onChange={(e) => setDiamond(e.target.value)}
              inputProps={{ min: 1, step: 1 }}
              required
            />
          </Grid>
        </Grid>
      </form>
    </FormPage>
  );
}
